/* eslint-disable unicorn/no-unreadable-array-destructuring */
import { useEffect, useMemo } from "react";
import useSWR from "swr";
import useSWRInfinite from "swr/infinite";

import {
  acknowledgeLADSAlarm,
  confirmLADSAlarm,
  receiveAllLADSIncidents,
  reportLADSAlarm,
} from "../api/lads";
import { useAccessToken } from "../auth-provider";
import {
  applyIncidentChange,
  type IncidentChangePayload,
  mapLADSIncidentToIncident,
} from "../mapper/incidents";
import { useDevicesStore } from "../store/devices-store";
import { useIncidentsStore } from "../store/incidents-store";
import { ladsWebSocketService } from "../util/websocket-lads";
import type { Device, Incident } from "../view-model";
import { useActiveRoom } from "./use-rooms";

const receiveAllIncidents = async (
  page: string,
  token?: string,
  active?: boolean,
  status?: "NEW" | "RESOLVED" | "CONFIRMED" | "REPORTED" | "CLOSED",
  context?: string,
  severity?: "NOTICE" | "INFO" | "WARNING" | "ALERT" | "ERROR",
  lifecycle?: "open" | "resolved",
  room?: string
): Promise<{ incidents: Incident[]; pagesTotal: number }> => {
  const pageNumber = page ? Number.parseInt(page) : 1;
  const limit = 20;
  const offset = (pageNumber - 1) * limit;

  // Fetch incidents directly from LADS API with filters. `context` scopes the
  // query to a single device (DeviceSet/<id>) for the device Fehlermeldungen
  // tab; `room` scopes the error history to the active room (server-side, so
  // pagination stays correct); both undefined fetches across all devices.
  // `severity` + `lifecycle` let the device tab filter server-side so the
  // shown list equals the fetched list (required for correct infinite scroll).
  const response = await receiveAllLADSIncidents(
    room, // roomId (room scope for the error history)
    context, // context (device scope)
    severity, // severity filter (type: critical/warning)
    active, // active filter
    status, // status filter
    limit,
    offset,
    token,
    lifecycle // lifecycle filter (open/resolved — strict-mode aware, server-side)
  );

  // Map LADS incidents to view-model incidents
  const mappedIncidents = (response.data || []).map((incident) =>
    mapLADSIncidentToIncident(incident)
  );

  const total = response.meta?.items?.total || 0;
  const pagesTotal = Math.ceil(total / limit);

  return {
    incidents: mappedIncidents,
    pagesTotal,
  };
};

const receiveActiveIncidents = async (token?: string): Promise<Incident[]> => {
  // Use the new API with active=true filter
  const response = await receiveAllLADSIncidents(
    undefined, // roomId
    undefined, // context
    undefined, // severity
    true, // active: true for active incidents
    undefined, // status
    undefined, // limit
    undefined, // offset
    token
  );

  // Map LADS incidents to view-model incidents. Globally (Dashboard,
  // Workplaces, Room-Switch, Device-Detail/Aktuelle-Werte, Alarm-Image) we
  // surface every alarm whose OPC-UA condition is currently raised. Operator
  // ack/confirm/report actions do NOT silence the global signal — only the
  // underlying source returning to normal (active === false) does. Operator
  // bookkeeping lives in the device-detail Fehlermeldungen list.
  const mappedIncidents = (response.data || [])
    .map((incident) => mapLADSIncidentToIncident(incident))
    .filter((incident) => incident.active === true);

  return mappedIncidents;
};

export function useIncidents(
  active?: boolean,
  status?: "NEW" | "RESOLVED" | "CONFIRMED" | "REPORTED" | "CLOSED",
  device?: Device,
  severity?: "NOTICE" | "INFO" | "WARNING" | "ALERT" | "ERROR",
  lifecycle?: "open" | "resolved"
) {
  const { setAllIncidents, setError, setLoading } = useIncidentsStore();
  const accessToken = useAccessToken();
  const { activeRoom } = useActiveRoom();

  // Room scope for the error history (no single device): mirror useAllDevices so
  // the incident list and the room-scoped device list share the same filter and
  // every incident resolves its device. A device query keeps its context scope.
  const room = device ? undefined : activeRoom?.id;

  // Device scope: with a device the query is restricted to that device's
  // OPC-UA context (DeviceSet/<id>) for the Fehlermeldungen tab; without it the
  // query spans all devices for the global error history. The id is also part
  // of the SWR key so each device keeps its own cached page list.
  const context = device ? `DeviceSet/${device.loId || device.id}` : undefined;
  const deviceKey = device ? `&device=${device.loId || device.id}` : "";

  // Every filter is part of the SWR key so each filter combination keeps its
  // own cached page list and refetches on change. severity + lifecycle drive
  // the device tab's server-side filtering.
  const filterKey =
    `${active === undefined ? "" : `&active=${active}`}` +
    `${status ? `&status=${status}` : ""}` +
    `${severity ? `&severity=${severity}` : ""}` +
    `${lifecycle ? `&lifecycle=${lifecycle}` : ""}` +
    `${room ? `&room=${room}` : ""}` +
    deviceKey;

  // Create key function that includes filter parameters
  const getKey = (
    index: number,
    previousPageData: { incidents: Incident[]; pagesTotal: number }[] | null
  ) => {
    // If no previous data (first call), fetch first page
    if (previousPageData === null) {
      return `allIncidents&page=1${filterKey}&token=${accessToken}`;
    }
    // If we have previous data, check if we've reached the last page
    if (previousPageData.length > 0) {
      const lastPage = previousPageData.at(-1);
      if (lastPage && lastPage.pagesTotal <= previousPageData.length) {
        return null; // Reached last page
      }
    }
    // Fetch next page
    return `allIncidents&page=${index + 1}${filterKey}&token=${accessToken}`;
  };

  // Use SWRInfinite for pagination
  const {
    data,
    error: swrError,
    isLoading: swrLoading,
    isValidating,
    setSize,
    size,
  } = useSWRInfinite(
    getKey,
    (key) => {
      const pageMatch = key.match(/page=(\d+)/);
      const page = pageMatch ? pageMatch[1] : "1";
      return receiveAllIncidents(
        page,
        accessToken || undefined,
        active,
        status,
        context,
        severity,
        lifecycle,
        room
      );
    },
    {
      refreshInterval: 0, // Disable automatic polling
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Flatten all incidents from all pages
  const incidents = useMemo(
    () => (data ? data.flatMap((page) => page.incidents) : []),
    [data]
  );
  const isLoadingInitialData = !data && !swrError;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 &&
      data &&
      data[size - 1] === undefined &&
      data[0] &&
      size < data[0].pagesTotal);

  // Sync SWR data to Zustand. Only the global (device-less) query owns the
  // shared allIncidents store — a device-scoped query must not overwrite it
  // with its filtered slice.
  useEffect(() => {
    if (!device && data && data.length > 0) {
      setAllIncidents(incidents);
      setLoading(false);
      setError(null);
    }
  }, [device, data, incidents, setAllIncidents, setLoading, setError]);

  // Handle SWR errors
  useEffect(() => {
    if (swrError) {
      setError(swrError);
      setLoading(false);
    }
  }, [swrError, setError, setLoading]);

  // Refetch on alarm transitions (replaces former 1s setInterval poll).
  useEffect(
    () =>
      ladsWebSocketService.onIncidentChanged(() => {
        setSize(size);
      }),
    [setSize, size]
  );

  if (data && data.length > 0) {
    const pagesTotal = data[0].pagesTotal;
    return {
      incidents,
      error: swrError,
      isLoading: swrLoading,
      isLoadingMore,
      size,
      setSize,
      isValidating,
      pagesTotal,
    };
  }

  return {
    incidents: incidents,
    error: swrError,
    isLoading: swrLoading,
    isLoadingMore,
    size,
    setSize,
    isValidating,
  };
}

// useEffect works on object identity.
// Therefore, the identical object has to be return in case of an error
const INCIDENT_EMPTY_FALLBACK_DATA: Incident[] = [];

export function useActiveIncidents() {
  const accessToken = useAccessToken();
  const { setActiveIncidents, setError, setLoading } = useIncidentsStore();

  // Use SWR for initial data fetching and caching
  const {
    data,
    error: swrError,
    mutate,
  } = useSWR(
    ["allActiveIncidents", accessToken || undefined],
    ([, token]) => receiveActiveIncidents(token).then((r) => r),
    {
      refreshInterval: 0, // Disable automatic polling
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Sync SWR data to Zustand
  useEffect(() => {
    if (data) {
      setActiveIncidents(data);
      setLoading(false);
      setError(null);
    }
  }, [data, accessToken, setActiveIncidents, setLoading, setError]);

  // Handle SWR errors
  useEffect(() => {
    if (swrError) {
      setError(swrError);
      setLoading(false);
    }
  }, [swrError, setError, setLoading]);

  useEffect(
    () =>
      ladsWebSocketService.onIncidentChanged((change) => {
        const ev = change as IncidentChangePayload;
        // Payload zu dünn für einen lokalen Insert → voller Refetch.
        if (applyIncidentChange([], ev) === "refetch") {
          mutate();
          return;
        }
        mutate(
          (current) => applyIncidentChange(current ?? [], ev) as Incident[],
          false
        );
        // Raise-Events tragen in Mount-Räumen den ALIAS-Pfad des Raum-Fanouts.
        // Der ist bei gleichnamigen gemounteten Functions (LCU-875-Zwillinge)
        // ambig — beide Kacheln würden färben. Der Refetch ersetzt die
        // Alias-Zeile durch die kanonische REST-Zeile (context), die exakt
        // eine Kachel trifft. Optimistic insert bleibt fürs sofortige Badge.
        if (ev.active && !ev.auditAction) {
          void mutate();
        }
      }),
    [mutate]
  );

  return {
    incidents: data || INCIDENT_EMPTY_FALLBACK_DATA,
    isLoading: !data && !swrError,
    error: swrError,
    mutate,
  };
}

// Pure Logik lebt im Mapper (dort testbar ohne window/auth-Kette);
// Re-Export für die bestehenden Consumer.
export {
  getIncidentCountForDevices,
  getIncidentsForDevice,
  resolveIncidentDevice,
} from "../mapper/incidents";

/**
 * Operator audit action. Each value selects exactly one OPC-UA event type
 * AND the matching sticky flag on the incident view-model:
 *   - "ack"     → AuditConditionAcknowledgeEventType  → incident.acknowledged
 *   - "confirm" → AuditConditionConfirmEventType      → incident.confirmed
 *   - "report"  → AuditConditionReportedEventType     → incident.reported
 *                 (Essentim companion spec, ns=…;i=15001)
 */
export type IncidentAction = "ack" | "confirm" | "report";

export type UpdateIncidentStatesProps = {
  incident: Incident;
  device: Device;
  token: string;
  action: IncidentAction;
  /** Operator comment recorded with the audit event. */
  comment?: string;
};

const FLAG_FOR_ACTION: Record<
  IncidentAction,
  "acknowledged" | "confirmed" | "reported"
> = {
  ack: "acknowledged",
  confirm: "confirmed",
  report: "reported",
};

export const updateIncidentStates = async ({
  action,
  comment,
  device,
  incident,
  token,
}: UpdateIncidentStatesProps) => {
  // Optimistic local store updates so the UI reflects the change immediately;
  // the SWR poller will reconcile with the backend's folded view 1s later.
  const stamp = Date.now().toString();
  let stampKey = "reportedAt";
  if (action === "ack") {
    stampKey = "acknowledgedAt";
  } else if (action === "confirm") {
    stampKey = "confirmedAt";
  }
  const newIncidentState = { [incident.id]: { [stampKey]: stamp } };
  useDevicesStore.getState().updateDeviceIncidentStates(device.id, {
    ...device.incidentStates,
    ...newIncidentState,
  });

  // Optimistic flag flip — sticky, never unset by reactivation; only the
  // underlying OPC-UA condition resolving (active=false) marks an alarm as
  // fixed/Behoben.
  const flagKey = FLAG_FOR_ACTION[action];
  const wasAlreadySet = incident[flagKey] === true;
  if (!wasAlreadySet) {
    useIncidentsStore
      .getState()
      .patchIncident(incident.id, device.id, { [flagKey]: true });
  }

  // Backend round-trip: raises an OPC-UA AuditCondition…EventType on the
  // alarm's source node. The bubbled event lands in lads_alarm_events, and
  // IncidentsService.foldToIncident derives the new lifecycle status on the
  // next read.
  try {
    switch (action) {
      case "ack": {
        await acknowledgeLADSAlarm(incident.id, comment, token);
        break;
      }
      case "confirm": {
        await confirmLADSAlarm(incident.id, comment, token);
        break;
      }
      case "report": {
        await reportLADSAlarm(incident.id, comment, token);
        break;
      }
      default: {
        const _exhaustive: never = action;
        throw new Error(`Unknown incident action: ${String(_exhaustive)}`);
      }
    }
  } catch (error) {
    // Roll back the optimistic flag if we set it, so the UI doesn't lie when
    // the backend call failed (e.g. incident already gone, address space
    // disconnect).
    if (!wasAlreadySet) {
      useIncidentsStore
        .getState()
        .patchIncident(incident.id, device.id, { [flagKey]: false });
    }
    throw error;
  }
};
